// One sign-in for every *.aroundtableround.com site (2026-10-09, Matt: "it's
// really annoying that people have to sign in on around table round and then
// also sign in for each of the other apps").
//
// HOUSE MODULE — byte-identical in every hub game; edit it in one repo and copy
// it to the rest (tools/house-drift.mjs checks). The server half is the
// `atr-session` Lambda (around-table-round/aws-lambda/atr-session.js), reached
// at /__/session/* on this site's own CloudFront distribution.
//
// Firebase keeps a session per origin, so each game used to need its own
// Google popup. Now a real sign-in anywhere also sets a domain-wide session
// cookie, and the readable `atr_signin` cookie tells every other site what to
// do on its next load:
//
//   atr_signin=in   + no local session  -> trade the cookie for a custom token
//                                          and sign in quietly (adopt)
//   atr_signin=out  + a local session   -> signed out somewhere else: sign out
//   no atr_signin   + a local session   -> signed in from before this existed:
//                                          share it, so the others pick it up
//   atr_signin=in   + a local session   -> roll the 90 days forward, once a day
//
// The store calls `settleSharedSignIn(auth, user)` first thing in its
// onAuthStateChanged listener and returns early when it says true (the
// session is being switched; the listener fires again). `markInteractiveSignIn()`
// goes right before the popup, `endSharedSignIn()` right before signOut.
// Off the hub's domain (localhost, tests) every function is a no-op.
import { signInWithCustomToken, signOut } from 'firebase/auth';

const RENEW_KEY = 'atr_signin_renewed';
const DAY = 24 * 60 * 60 * 1000;

let adoptTried = false;
let interactive = false;
let forced = null;

export const sharedSignInEnabled = () => forced ?? (typeof window !== 'undefined'
  && /(^|\.)aroundtableround\.com$/.test(window.location.hostname));

export function readMarker(cookieString = typeof document !== 'undefined' ? document.cookie : '') {
  const match = cookieString.match(/(?:^|;\s*)atr_signin=([^;]*)/);
  return match ? match[1] : null;
}

const post = (path, body) => fetch(`/__/session/${path}`, {
  method: 'POST',
  credentials: 'same-origin',
  headers: body ? { 'content-type': 'application/json' } : undefined,
  body: body ? JSON.stringify(body) : undefined,
});

async function share(user) {
  try {
    const res = await post('start', { idToken: await user.getIdToken() });
    if (res.ok) {
      try { localStorage.setItem(RENEW_KEY, String(Date.now())); } catch { /* private mode */ }
    }
  } catch { /* the next load tries again */ }
}

function dueForRenewal() {
  try { return Date.now() - Number(localStorage.getItem(RENEW_KEY) || 0) > DAY; } catch { return false; }
}

// Returns true when it is switching the session (the listener will fire again
// with the new user), false when the store should carry on with `user`.
export async function settleSharedSignIn(auth, user) {
  if (!sharedSignInEnabled() || !auth) return false;
  const marker = readMarker();
  const real = user && !user.isAnonymous;

  if (!real) {
    if (marker !== 'in' || adoptTried) return false;
    adoptTried = true;
    try {
      const res = await post('token');
      if (!res.ok) return false;
      const { token } = await res.json();
      await signInWithCustomToken(auth, token);
      return true;
    } catch {
      return false;
    }
  }

  if (interactive) {
    interactive = false;
    share(user);
    return false;
  }
  if (marker === 'out') {
    await signOut(auth);
    return true;
  }
  if (marker !== 'in' || dueForRenewal()) share(user);
  return false;
}

export function markInteractiveSignIn() {
  interactive = true;
}

export async function endSharedSignIn() {
  if (!sharedSignInEnabled()) return;
  try { await post('end'); } catch { /* signing out locally still happens */ }
  try { localStorage.removeItem(RENEW_KEY); } catch { /* private mode */ }
}

export const _test = { reset(enabled = null) { adoptTried = false; interactive = false; forced = enabled; } };
